// src/hooks/useGetCalendarEntries.ts
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import { ICalendarEntry, calendarEntrySchema } from '../Interfaces/ICalendarEntry';
import { z } from 'zod';

export const useGetCalendarEntries = () => {
    const { data, isLoading } = useQuery<ICalendarEntry[]>({
        queryKey: ['calendarEntries'],
        queryFn: async () => {
            const fetchedData = await customFetcher<ICalendarEntry[]>({ endpoint: '/calendar-entries/' });
            // Validierung der Daten mit Zod:
            const parsedData = z.array(calendarEntrySchema).parse(fetchedData);
            return parsedData;
        },
        initialData: [],
        refetchOnWindowFocus: false,
    });

    return { calendarEntries: data, isLoadingCalendarEntries: isLoading };
};

export const useGetCalendarEntry = (entryId?: number, initialEntry?: ICalendarEntry | null) => {
    const { data, isLoading, isFetched, isError } = useQuery<ICalendarEntry | null>({
        queryKey: ['calendarEntry', entryId ?? 0],
        queryFn: async () => {
            if (!entryId) {
                return null;
            }
            const fetchedData = await customFetcher<ICalendarEntry>({ endpoint: `/calendar-entries/${entryId}/` });
            return calendarEntrySchema.parse(fetchedData);
        },
        enabled: Boolean(entryId),
        initialData: initialEntry ?? null,
        refetchOnWindowFocus: false,
        // Ein Deep-Link auf eine geloeschte/unbekannte ID soll sofort als Fehler
        // ankommen statt dreimal nachzufassen.
        retry: false,
    });

    return {
        calendarEntry: data,
        isLoadingCalendarEntry: isLoading,
        // `initialData` sorgt dafuer, dass `isLoading` nie true wird, und `isFetching`
        // ist auf dem ersten Render noch nicht verlaesslich. `isFetched` wird erst nach
        // der abgeschlossenen Anfrage true — nur darauf darf ein "gibt es nicht"-Schluss
        // beruhen, sonst springt ein gueltiger Deep-Link sofort zurueck zur Liste.
        isFetchedCalendarEntry: isFetched,
        isErrorCalendarEntry: isError,
    };
};

export const useUpdateCalendarEntry = () => {
    const queryClient = useQueryClient();

    return useMutation<ICalendarEntry, Error, ICalendarEntry>({
        mutationFn: async (updatedEntry: ICalendarEntry) => {
            const endpoint = updatedEntry.id
                ? `/calendar-entries/${updatedEntry.id}/`
                : '/calendar-entries/';
            const method = updatedEntry.id ? 'PUT' : 'POST';
            const payload: ICalendarEntry = calendarEntrySchema.parse({
                id: updatedEntry.id,
                title: updatedEntry.title,
                description: updatedEntry.description ?? null,
                start_time: updatedEntry.start_time,
                end_time: updatedEntry.end_time,
                all_day: updatedEntry.all_day,
                location: updatedEntry.location ?? null,
                calendar_type: updatedEntry.calendar_type,
                tag_ids: updatedEntry.tag_ids ?? (updatedEntry.tags ?? []).map((tag) => tag.id),
            });

            const data = await customFetcher<ICalendarEntry>({
                endpoint,
                method,
                body: payload,
            });
            // Validierung der Antwort mit Zod
            const parsedData = calendarEntrySchema.parse(data);
            return parsedData;
        },
        onSuccess: () => {
            // Nach erfolgreichem Speichern den Cache für calendarEntries invalidieren,
            // damit die Liste neu geladen wird.
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntry'] });
        },
    });
};

export const useDeleteCalendarEntry = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, number>({
        mutationFn: async (calendarEntryId: number) => {
            await customFetcher({
                endpoint: `/calendar-entries/${calendarEntryId}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntry'] });
        },
    });
};

export const useToggleCalendarEntryFavorite = () => {
    const queryClient = useQueryClient();

    return useMutation<{ event_id: number; is_favorite: boolean }, Error, { eventId: number; isFavorite: boolean }>({
        mutationFn: async ({ eventId, isFavorite }) => {
            return customFetcher<{ event_id: number; is_favorite: boolean }>({
                endpoint: `/calendar-entries/${eventId}/favorite/`,
                method: isFavorite ? 'PUT' : 'DELETE',
            });
        },
        onSuccess: (_result, variables) => {
            queryClient.setQueryData<ICalendarEntry[]>(['calendarEntries'], (previousEntries = []) =>
                previousEntries.map((entry) => (
                    entry.id === variables.eventId
                        ? { ...entry, is_favorite: variables.isFavorite }
                        : entry
                ))
            );
            queryClient.setQueryData<ICalendarEntry | null>(['calendarEntry', variables.eventId], (previousEntry) => (
                previousEntry
                    ? { ...previousEntry, is_favorite: variables.isFavorite }
                    : previousEntry
            ));
            void queryClient.invalidateQueries({ queryKey: ['calendarEntries'] });
            void queryClient.invalidateQueries({ queryKey: ['calendarEntry', variables.eventId] });
        },
    });
};
